/**
 * Route definitions with React.lazy() imports for code splitting
 */

import { lazy } from 'react'

// Lazy imports for page components - Route-based code splitting
export const LandingPage = lazy(() =>
  import('@/components/LandingPage').then((module) => ({ default: module.LandingPage })),
)
export const BalancesPage = lazy(() =>
  import('@/modules/accounting/features/balances').then((module) => ({
    default: module.BalancesPage,
  })),
)
export const TransactionsPage = lazy(() =>
  import('@/modules/accounting/features/transactions/components/TransactionsPage').then(
    (module) => ({ default: module.TransactionsPage }),
  ),
)
export const ImportPage = lazy(() =>
  import('@/modules/accounting/features/import').then((module) => ({ default: module.ImportPage })),
)
export const UserCustomImporterPage = lazy(() =>
  import('@/modules/accounting/features/import/pages').then((module) => ({
    default: module.UserCustomImporterPage,
  })),
)
export const MatchingPage = lazy(() =>
  import('@/modules/accounting/features/transaction-matching').then((module) => ({
    default: module.MatchingPage,
  })),
)
export const MissingPricesPage = lazy(() =>
  import('@/modules/accounting/features/portfolio').then((module) => ({
    default: module.MissingPricesPage,
  })),
)
export const TaxReportPage = lazy(() =>
  import('@/modules/accounting/features/tax-reports/TaxReportPage').then((module) => ({
    default: module.TaxReportPage,
  })),
)
export const SuperAdminLayout = lazy(() =>
  import('@/modules/admin').then((m) => ({ default: m.SuperAdminLayout })),
)
export const OrgAdminLayout = lazy(() =>
  import('@/modules/admin').then((m) => ({ default: m.OrgAdminLayout })),
)
export const SystemDashboardPage = lazy(() =>
  import('@/modules/admin/features/super-admin/dashboard').then((m) => ({
    default: m.SystemDashboardPage,
  })),
)
export const TenantsPage = lazy(() =>
  import('@/modules/admin/features/super-admin/tenants').then((m) => ({ default: m.TenantsPage })),
)
export const TenantDetailPage = lazy(() =>
  import('@/modules/admin/features/super-admin/tenants').then((m) => ({
    default: m.TenantDetailPage,
  })),
)
export const SystemUsersPage = lazy(() =>
  import('@/modules/admin/features/super-admin/users').then((m) => ({
    default: m.SystemUsersPage,
  })),
)
export const UserDetailPage = lazy(() =>
  import('@/modules/admin/features/super-admin/users').then((m) => ({
    default: m.UserDetailPage,
  })),
)
export const AuditActivityPage = lazy(() =>
  import('@/modules/admin/features/super-admin/audit-log').then((m) => ({
    default: m.AuditActivityPage,
  })),
)
export const TicketsPage = lazy(() =>
  import('@/modules/admin/features/super-admin/support').then((m) => ({ default: m.TicketsPage })),
)
export const TicketDetailPage = lazy(() =>
  import('@/modules/admin/features/super-admin/support').then((m) => ({
    default: m.TicketDetailPage,
  })),
)
export const ClientsOverviewPage = lazy(
  () => import('@/platform/workspaces/pages/ClientsOverviewPage'),
)
export const MyTicketsPage = lazy(() =>
  import('@/modules/user/features/support').then((m) => ({ default: m.MyTicketsPage })),
)
export const MyTicketDetailPage = lazy(() =>
  import('@/modules/user/features/support').then((m) => ({
    default: m.MyTicketDetailPage,
  })),
)
export const ImportMappingsPage = lazy(() =>
  import('@/modules/admin/features/import-mappings').then((m) => ({
    default: m.ImportMappingsPage,
  })),
)
export const AdminCustomImporterPage = lazy(() =>
  import('@/modules/admin/features/import-mappings').then((m) => ({
    default: m.AdminCustomImporterPage,
  })),
)
// «Import adapters» (exchange APIs and blockchains, one lifecycle) and its unknown-contracts queue: they replace the
// blockchain templates / networks / classification screens.
export const AdaptersPage = lazy(() =>
  import('@/modules/admin/features/super-admin/adapters').then((m) => ({ default: m.AdaptersPage })),
)
export const UnknownContractsPage = lazy(() =>
  import('@/modules/admin/features/super-admin/adapters').then((m) => ({ default: m.UnknownContractsPage })),
)
export const MarketDataInventoryTab = lazy(() =>
  import('@/modules/admin/features/super-admin/market-data').then((m) => ({
    default: m.MarketDataInventoryTab,
  })),
)
export const MarketDataDownloadTab = lazy(() =>
  import('@/modules/admin/features/super-admin/market-data').then((m) => ({
    default: m.MarketDataDownloadTab,
  })),
)
export const MarketDataJobsTab = lazy(() =>
  import('@/modules/admin/features/super-admin/market-data').then((m) => ({
    default: m.MarketDataJobsTab,
  })),
)
export const AssetsPage = lazy(() =>
  import('@/modules/admin/features/super-admin/assets').then((m) => ({ default: m.AssetsPage })),
)
export const AssetDetailPage = lazy(() =>
  import('@/modules/admin/features/super-admin/assets').then((m) => ({
    default: m.AssetDetailPage,
  })),
)
export const AssetFormPage = lazy(() =>
  import('@/modules/admin/features/super-admin/assets').then((m) => ({ default: m.AssetFormPage })),
)
export const AssetImportPage = lazy(() =>
  import('@/modules/admin/features/super-admin/assets').then((m) => ({
    default: m.AssetImportPage,
  })),
)
export const EmailTemplatesPage = lazy(() =>
  import('@/modules/admin/features/super-admin/email').then((m) => ({
    default: m.EmailTemplatesPage,
  })),
)
export const EmailLogsPage = lazy(() =>
  import('@/modules/admin/features/super-admin/email').then((m) => ({ default: m.EmailLogsPage })),
)
export const EmailSettingsPage = lazy(() =>
  import('@/modules/admin/features/super-admin/email').then((m) => ({
    default: m.EmailSettingsPage,
  })),
)
export const JobsPage = lazy(() =>
  import('@/modules/admin/features/jobs/pages/JobsPage').then((m) => ({ default: m.JobsPage })),
)
export const OrgDashboardPage = lazy(() =>
  import('@/modules/admin/features/org-admin/dashboard').then((m) => ({
    default: m.OrgDashboardPage,
  })),
)
export const OrgUsersPage = lazy(() =>
  import('@/modules/admin/features/org-admin/users').then((m) => ({ default: m.OrgUsersPage })),
)
export const OrgSettingsPage = lazy(() =>
  import('@/modules/admin/features/org-admin/settings').then((m) => ({
    default: m.OrgSettingsPage,
  })),
)
export const OrgBillingPage = lazy(() =>
  import('@/modules/admin/features/org-admin/billing').then((m) => ({ default: m.OrgBillingPage })),
)
export const OrgAccountantsPage = lazy(() =>
  import('@/modules/admin/features/org-admin/accountants').then((m) => ({
    default: m.OrgAccountantsPage,
  })),
)
export const OrgNotificationsPage = lazy(() =>
  import('@/modules/admin/features/org-admin/notifications/components/NotificationInbox').then(
    (m) => ({ default: m.NotificationInbox }),
  ),
)
export const OrgExportPage = lazy(() =>
  import('@/modules/admin/features/org-admin/export').then((m) => ({ default: m.OrgExportPage })),
)
export const OrgReportsPage = lazy(() =>
  import('@/modules/admin/features/org-admin/reports').then((m) => ({ default: m.OrgReportsPage })),
)
export const OrgActivityPage = lazy(() =>
  import('@/modules/admin/features/org-admin/activity').then((m) => ({
    default: m.OrgActivityPage,
  })),
)
export const OrgApiKeysPage = lazy(() =>
  import('@/modules/admin/features/org-admin/api-keys').then((m) => ({
    default: m.OrgApiKeysPage,
  })),
)
export const OrgPlatformApiKeysPage = lazy(() =>
  import('@/modules/admin/features/org-admin/platform-api-keys').then((m) => ({
    default: m.OrgPlatformApiKeysPage,
  })),
)
export const WebhooksPage = lazy(() =>
  import('@/modules/admin/features/org-admin/webhooks').then((m) => ({ default: m.WebhooksPage })),
)
export const PermissionsManagementPage = lazy(() =>
  import('@/modules/admin/features/org-admin/permissions').then((m) => ({
    default: m.PermissionsManagementPage,
  })),
)
export const OrgJobsPage = lazy(() =>
  import('@/modules/admin/features/org-jobs').then((m) => ({ default: m.OrgJobsPage })),
)
export const SsoSettingsPage = lazy(() =>
  import('@/modules/admin/features/org-admin/sso/SsoSettingsPage').then((m) => ({
    default: m.default,
  })),
)
export const DataRetentionPage = lazy(() =>
  import('@/modules/admin/features/org-admin/data-retention/DataRetentionPage').then((m) => ({
    default: m.DataRetentionPage,
  })),
)
export const TeamsPage = lazy(() =>
  import('@/modules/admin/features/org-admin/teams/pages/TeamsPage').then((m) => ({
    default: m.TeamsPage,
  })),
)
export const OrgRolesPage = lazy(() =>
  import('@/modules/admin/features/org-admin/roles').then((m) => ({ default: m.OrgRolesPage })),
)
export const LoginPage = lazy(() =>
  import('@/platform/auth/components/LoginPage').then((module) => ({ default: module.LoginPage })),
)
export const RegisterPage = lazy(() =>
  import('@/platform/auth/components/RegisterPage').then((module) => ({
    default: module.RegisterPage,
  })),
)
export const WaitlistLinkPage = lazy(() =>
  import('@/platform/auth/components/WaitlistLinkPage').then((module) => ({
    default: module.WaitlistLinkPage,
  })),
)
export const ForgotPasswordPage = lazy(() =>
  import('@/platform/auth/components/ForgotPasswordPage').then((module) => ({
    default: module.ForgotPasswordPage,
  })),
)
export const ResetPasswordPage = lazy(() =>
  import('@/platform/auth/components/ResetPasswordPage').then((module) => ({
    default: module.ResetPasswordPage,
  })),
)
export const VerifyEmailPage = lazy(() =>
  import('@/platform/auth/components/VerifyEmailPage').then((module) => ({
    default: module.VerifyEmailPage,
  })),
)
export const MagicLinkRequestPage = lazy(() =>
  import('@/platform/auth/components/MagicLinkRequestPage').then((module) => ({
    default: module.MagicLinkRequestPage,
  })),
)
export const MagicLinkLoginPage = lazy(() =>
  import('@/platform/auth/components/MagicLinkLoginPage').then((module) => ({
    default: module.MagicLinkLoginPage,
  })),
)
export const ProfilePage = lazy(() =>
  import('@/platform/profile/ProfilePage').then((module) => ({ default: module.ProfilePage })),
)
export const EmailChangePage = lazy(() =>
  import('@/platform/profile/EmailChangePage').then((module) => ({
    default: module.EmailChangePage,
  })),
)
// SuperAdmin pages
export const RevenueDashboardPage = lazy(() =>
  import('@/modules/admin/features/super-admin/revenue').then((m) => ({
    default: m.RevenueDashboardPage,
  })),
)
export const MaintenancePage = lazy(() =>
  import('@/modules/admin/features/super-admin/maintenance').then((m) => ({
    default: m.MaintenancePage,
  })),
)
export const PricingPlansPage = lazy(() =>
  import('@/modules/admin/features/super-admin/pricing-plans').then((m) => ({
    default: m.PricingPlansPage,
  })),
)
export const WaitlistPage = lazy(() =>
  import('@/modules/admin/features/super-admin/waitlist').then((m) => ({
    default: m.WaitlistPage,
  })),
)
export const RateLimitingPage = lazy(() =>
  import('@/modules/admin/features/super-admin/rate-limiting').then((m) => ({
    default: m.RateLimitingPage,
  })),
)
export const ReadinessPage = lazy(() =>
  import('@/modules/admin/features/super-admin/readiness/ReadinessPage').then((m) => ({
    default: m.ReadinessPage,
  })),
)
export const TaxRulesPage = lazy(() =>
  import('@/modules/admin/features/super-admin/tax-config').then((m) => ({
    default: m.TaxRulesPage,
  })),
)
export const ExchangesPage = lazy(() =>
  import('@/modules/admin/features/super-admin/exchanges').then((m) => ({
    default: m.ExchangesPage,
  })),
)
// Rich API template editor page (issue #359 — Phase 2a foundation). Lives at
// `/admin/system/api-import-mappings/templates/:id/edit`. Re-exported from the exchanges
// feature under `ExchangeTemplateEditorPage`, which says which template editor it is.
export const ExchangeTemplateEditorPage = lazy(() =>
  import('@/modules/admin/features/super-admin/exchanges').then((m) => ({
    default: m.ExchangeTemplateEditorPage,
  })),
)
export const TransactionAttributesPage = lazy(() =>
  import('@/modules/admin/features/super-admin/transaction-attributes').then((m) => ({
    default: m.AttributesPage,
  })),
)

// Simple catalog admin CRUD (BUG-80 / PR2) and the «Ajustes» page (#2828, which hosts
// Languages) live in `super-admin-routes.tsx` to keep this file ≤500 lines (used only
// inside the super-admin routes subtree).
export const ApiImportMappingsListPage = lazy(() =>
  import('@/modules/admin/features/super-admin/api-import-mappings').then((m) => ({
    default: m.ApiImportMappingsListPage,
  })),
)
export const ApiImportMappingsDetailPage = lazy(() =>
  import('@/modules/admin/features/super-admin/api-import-mappings').then((m) => ({
    default: m.ApiImportMappingsDetailPage,
  })),
)
export const ApiImportMappingsCreatePage = lazy(() =>
  import('@/modules/admin/features/super-admin/api-import-mappings').then((m) => ({
    default: m.ApiImportMappingsCreatePage,
  })),
)
export const PermissionsMatrixPage = lazy(() =>
  import('@/modules/admin/features/super-admin/permissions-matrix').then((m) => ({
    default: m.PermissionsMatrixPage,
  })),
)
export const SSOManagementPage = lazy(() =>
  import('@/modules/admin/features/super-admin/sso-management').then((m) => ({
    default: m.SSOManagementPage,
  })),
)
export const AuthAuditPage = lazy(() =>
  import('@/modules/admin/features/super-admin/auth-audit').then((m) => ({
    default: m.AuthAuditPage,
  })),
)
// Additional SuperAdmin pages
export const AuditReportsPage = lazy(() =>
  import('@/modules/admin/features/super-admin/audit-reports').then((m) => ({
    default: m.AuditReportsPage,
  })),
)
export const GdprManagementPage = lazy(() =>
  import('@/modules/admin/features/super-admin/gdpr').then((m) => ({
    default: m.GdprManagementPage,
  })),
)
export const BroadcastsPage = lazy(() =>
  import('@/modules/admin/features/super-admin/notifications').then((m) => ({
    default: m.BroadcastsPage,
  })),
)
// Bulk Operations Pages
export const BulkOperationsPage = lazy(() =>
  import('@/modules/admin/features/bulk-operations').then((m) => ({
    default: m.BulkOperationsPage,
  })),
)
export const BulkOperationDetailPage = lazy(() =>
  import('@/modules/admin/features/bulk-operations').then((m) => ({
    default: m.BulkOperationDetailPage,
  })),
)
